import React from 'react';
import dayjs from 'dayjs';
import { DatePicker } from 'antd';
import styles from './index.module.scss';
import { setPostImage } from '@images/imageUrls';

interface VoteOption {
    id: string | number;
    text: string;
    isExisting?: boolean; // 是否是已存在的选项（有真实id）
}

interface VoteModalProps {
    visible: boolean;
    onClose?: () => void;
    onConfirm?: (voteData: any) => void;
    initialData?: any;
    // 新增：支持多投票场景
    votes?: any[];
    onSave?: (votes: any[]) => void;
    onCancel?: () => void;
}

interface VoteModalState {
    title: string;
    options: VoteOption[];
    voteType: 'single' | 'multiple';
    endTime: string;
    isClosing: boolean;
    nextId: number;
}

class VoteModal extends React.Component<VoteModalProps, VoteModalState> {
    modalRef = React.createRef<HTMLDivElement>();

    constructor(props: VoteModalProps) {
        super(props);
        this.state = {
            title: '',
            options: [
                { id: '1', text: '' },
                { id: '2', text: '' },
            ],
            voteType: 'single',
            endTime: '',
            isClosing: false,
            nextId: 3,
        };
    }

    componentDidMount() {
        // 移除 mousedown 监听，改用 overlay 的 onClick
        // document.addEventListener('mousedown', this.handleClickOutside);
    }

    componentWillUnmount() {
        // document.removeEventListener('mousedown', this.handleClickOutside);
        document.body.style.overflow = 'unset';
    }

    componentDidUpdate(prevProps: VoteModalProps) {
        if (this.props.visible && !prevProps.visible) {
            // 弹窗打开
            this.setState({ isClosing: false });
            document.body.style.overflow = 'hidden';

            // 优先读取 votes 数据（支持多投票回显，这里取第一个）
            const dataSource = (this.props.votes && this.props.votes.length > 0) 
                ? this.props.votes[0] 
                : this.props.initialData;

            // 加载初始数据或设置默认值
            if (dataSource) {
                this.setState({
                    title: dataSource.title || dataSource.question || '',
                    options: dataSource.options && dataSource.options.length > 0
                        ? dataSource.options.map((opt: any, idx: number) => ({
                            // 保留原有的数字id（编辑时），新增选项使用临时字符串id
                            id: (typeof opt.id === 'number') ? opt.id : String(idx + 1),
                            text: opt.text || opt.content || '',
                            // 标记是否是原有的选项（有真实id）
                            isExisting: typeof opt.id === 'number',
                            // 保留所有原始字段（编辑时需要）
                            ...opt,
                            // 确保text字段正确
                            content: opt.text || opt.content || '',
                        }))
                        : [{ id: '1', text: '' }, { id: '2', text: '' }],
                    voteType: dataSource.voteType || (dataSource.choiceType === 2 ? 'multiple' : 'single'),
                    endTime: dataSource.endTime || dataSource.expiredAt || this.getDefaultEndTime(),
                    nextId: (dataSource.options?.length || 2) + 1,
                });
            } else {
                // 重置为默认值
                this.setState({
                    title: '',
                    options: [{ id: '1', text: '' }, { id: '2', text: '' }],
                    voteType: 'single',
                    endTime: this.getDefaultEndTime(),
                    nextId: 3,
                });
            }
        } else if (!this.props.visible && prevProps.visible) {
            // 弹窗关闭
            document.body.style.overflow = 'unset';
        }
    }

    getDefaultEndTime = () => {
        const date = new Date();
        date.setDate(date.getDate() + 30);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}`;
    }

    handleClickOutside = (e: MouseEvent) => {
        if (!this.props.visible) return;
        if (this.modalRef.current && !this.modalRef.current.contains(e.target as Node)) {
            this.handleClose();
        }
    }

    handleClose = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        e?.preventDefault();
        
        // 立即调用 onClose 或 onCancel
        this.props.onClose?.();
        this.props.onCancel?.();
        
        // 设置关闭动画状态
        this.setState({ isClosing: true });
        
        // 300ms 后重置 isClosing 状态
        setTimeout(() => {
            this.setState({ isClosing: false });
        }, 300);
    }

    handleAddOption = () => {
        if (this.state.options.length >= 100) return;
        const newId = this.state.nextId.toString();
        this.setState({
            options: [...this.state.options, { id: newId, text: '' }],
            nextId: this.state.nextId + 1,
        });
    }

    handleRemoveOption = (id: string | number) => {
        if (this.state.options.length <= 2) return;
        
        // 检查是否是已存在的选项（不允许删除）
        const option = this.state.options.find(opt => opt.id === id);
        if (option?.isExisting) {
            alert('不能删除已有选项');
            return;
        }
        
        this.setState({
            options: this.state.options.filter(opt => opt.id !== id),
        });
    }

    handleOptionChange = (id: string | number, text: string) => {
        this.setState({
            options: this.state.options.map(opt => opt.id === id ? { ...opt, text } : opt),
        });
    }

    handleConfirm = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        e?.preventDefault();

        if (!this.state.title.trim()) {
            alert('请输入投票标题');
            return;
        }

        const options = this.state.options;
        if (options.length < 2) {
            alert('至少需要2个有效选项');
            return;
        }

        const emptyOptionIndex = options.findIndex(opt => !opt.text.trim());
        if (emptyOptionIndex >= 0) {
            alert(`第${emptyOptionIndex + 1}个选项不能为空`);
            return;
        }

        // 计算距离结束还有多少天时分秒
        const endDate = new Date(this.state.endTime);
        const now = new Date();
        const diffTime = endDate.getTime() - now.getTime();

        const days = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diffTime % (1000 * 60)) / 1000);

        // 获取原始的vote数据（如果是编辑模式）
        const dataSource = (this.props.votes && this.props.votes.length > 0) 
            ? this.props.votes[0] 
            : this.props.initialData;

        const voteData = {
            // 保留所有原始字段（编辑时）
            ...(dataSource || {}),
            // 更新的字段
            title: this.state.title,
            options: options.map(opt => {
                // 如果是已有选项，保留所有原始字段
                if (opt.isExisting) {
                    return {
                        ...opt,
                        content: opt.text,
                        text: opt.text,
                    };
                }
                // 新增选项只包含基本字段
                return {
                    content: opt.text,
                    text: opt.text,
                };
            }),
            voteType: this.state.voteType,
            choiceType: this.state.voteType === 'single' ? 1 : 2,
            endTime: this.state.endTime,
            expiredAt: this.state.endTime,
            duration: {
                days: days > 0 ? days : 0,
                hours: hours > 0 ? hours : 0,
                minutes: minutes > 0 ? minutes : 0,
                seconds: seconds > 0 ? seconds : 0,
            },
        };

        // 先关闭弹窗
        this.props.onClose?.();
        this.props.onCancel?.();
        
        // 延迟一下再调用回调，确保弹窗已关闭
        setTimeout(() => {
            this.props.onConfirm?.(voteData);
            this.props.onSave?.([voteData]);
        }, 50);
    }

    render() {
        if (!this.props.visible && !this.state.isClosing) return null;

        return (
            <div 
                className={`${styles.modalOverlay} ${this.state.isClosing ? styles.modalOverlayClosing : ''}`}
                onClick={(e) => {
                    // 只有点击遮罩层本身才关闭，点击内容区不关闭
                    if (e.target === e.currentTarget) {
                        this.handleClose(e);
                    }
                }}
            >
                <div
                    className={`${styles.modalContent} ${this.state.isClosing ? styles.modalContentClosing : ''}`}
                    ref={this.modalRef}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* 关闭按钮 */}
                    <img
                        src={setPostImage.closeIcon}
                        alt="关闭"
                        className={styles.closeIcon}
                        onClick={this.handleClose}
                    />

                    {/* 标题 */}
                    <div className={styles.modalTitle}>投票</div>

                    {/* 投票标题 */}
                    <div className={styles.section}>
                        <div className={styles.sectionTitle}>
                            <span className={styles.required}>*</span>投票标题
                        </div>
                        <div className={styles.inputWrapper}>
                            <input
                                type="text"
                                placeholder="请输入投票标题"
                                className={styles.input}
                                value={this.state.title}
                                onChange={(e) => this.setState({ title: e.target.value })}
                                maxLength={40}
                            />
                            <span className={styles.charCount}>{this.state.title.length}/40</span>
                        </div>
                    </div>

                    {/* 投票类型 */}
                    <div className={styles.section}>
                        <div className={styles.sectionTitle}>
                            <span className={styles.required}>*</span>投票类型
                        </div>
                        <div className={styles.voteTypeWrapper}>
                            <div className={styles.radioItem} onClick={() => this.setState({ voteType: 'single' })}>
                                <img
                                    src={this.state.voteType === 'single'
                                        ? setPostImage.checkedActive
                                        : setPostImage.atCheckout
                                    }
                                    alt="radio"
                                    className={styles.radioIcon}
                                />
                                <span className={styles.radioLabel}>单选</span>
                            </div>
                            <div className={styles.radioItem} onClick={() => this.setState({ voteType: 'multiple' })}>
                                <img
                                    src={this.state.voteType === 'multiple'
                                        ? setPostImage.checkedActive
                                        : setPostImage.atCheckout
                                    }
                                    alt="radio"
                                    className={styles.radioIcon}
                                />
                                <span className={styles.radioLabel}>多选</span>
                            </div>
                        </div>
                    </div>

                    {/* 截止时间 */}
                    <div className={styles.section}>
                        <div className={styles.sectionTitle}>
                            <span className={styles.required}>*</span>截止时间
                        </div>
                        <div className={styles.inputWrapper}>
                            <DatePicker
                                showTime={{ format: 'HH:mm' }}
                                format="YYYY-MM-DD HH:mm"
                                className={styles.input}
                                value={this.state.endTime ? dayjs(this.state.endTime, 'YYYY-MM-DD HH:mm') : null}
                                onChange={(_, dateString) => {
                                    if (typeof dateString === 'string') {
                                        this.setState({ endTime: dateString });
                                    } else {
                                        this.setState({ endTime: '' });
                                    }
                                }}
                                placeholder="YYYY-MM-DD HH:mm"
                                allowClear={false}
                                style={{ width: '100%' }}
                                getPopupContainer={trigger => trigger.parentNode as HTMLElement}
                            />
                        </div>
                    </div>

                    {/* 投票选项 */}
                    <div className={styles.section}>
                        <div className={styles.sectionTitle}>
                            <span className={styles.required}>*</span>投票选项
                            <span className={styles.optionCount}>{this.state.options.length}/100</span>
                            <span className={styles.optionTip}>（最多100个选项）</span>
                        </div>
                        <div className={styles.optionsWrapper}>
                            {this.state.options.map((option, index) => (
                                <div key={option.id} className={styles.optionItem}>
                                    {this.state.options.length > 2 && (
                                        <img
                                            src={setPostImage.removeIcon}
                                            alt="删除"
                                            className={styles.removeIcon}
                                            onClick={() => this.handleRemoveOption(option.id)}
                                        />
                                    )}
                                    <input
                                        type="text"
                                        placeholder={`选项${index + 1}`}
                                        className={styles.input}
                                        value={option.text}
                                        onChange={(e) => this.handleOptionChange(option.id, e.target.value)}
                                        maxLength={20}
                                    />
                                    <span className={styles.charCount}>{option.text.length}/20</span>
                                </div>
                            ))}
                            {this.state.options.length < 100 && <div className={styles.addOption} onClick={this.handleAddOption}>
                                <img
                                    src={setPostImage.addIcon}
                                    alt="添加"
                                    className={styles.addIcon}
                                />
                                <span className={styles.addText}>添加选项</span>
                            </div>}
                        </div>
                    </div>

                    {/* 底部按钮 */}
                    <div className={styles.footer}>
                        <button type="button" className={styles.cancelBtn} onClick={this.handleClose}>
                            取消
                        </button>
                        <button type="button" className={styles.confirmBtn} onClick={this.handleConfirm}>
                            确定
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

export default VoteModal;
